'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const translations = {
  mn: {
    tagline: 'Өдөр тутмын мэдээлэл',
    heading1: 'Өнөөдөр юу',
    heading2: 'уншиж байна вэ?',
    subheading: 'Хамгийн сүүлийн үеийн мэдээг үзэхийн тулд ангилал сонгоно уу.',
    browse: 'Үзэх',
    copyright: 'Бүх эрх хуулиар хамгаалагдсан.',
    adminLogin: 'Админ нэвтрэх',
    adminEmail: 'И-мэйл',
    adminPassword: 'Нууц үг',
    adminSubmit: 'Нэвтрэх',
    adminTitle: 'Админ хэсэг',
    categories: [
      {
        id: 'legal',
        title: 'Хуулийн мэдээ',
        subtitle: '법률 뉴스',
        description: 'Монгол болон Солонгосын шүүхийн шийдвэр, хууль тогтоомжийн шинэчлэл, хуулийн дүн шинжилгээ.',
        tag: 'Pobtusa - Побтуса хуулийн мэдээ',
      },
      {
        id: 'financial',
        title: 'Санхүүгийн мэдээ',
        subtitle: '금융 뉴스',
        description: 'Зах зээл, эдийн засгийн бодлого, банкны зохицуулалт, хөрөнгө оруулалтын мэдээлэл.',
        tag: 'Hantusa - Хантуса санхүү',
      },
      {
        id: 'other',
        title: 'Бусад мэдээ',
        subtitle: '기타 뉴스',
        description: 'Улс төр, нийгэм, технологи болон бүс нутгийн ерөнхий мэдээ.',
        tag: 'Mongtusa - Мунтуса',
      },
    ],
  },
  kr: {
    tagline: '매일의 브리핑',
    heading1: '오늘은 무엇을',
    heading2: '읽으시겠어요?',
    subheading: '최신 뉴스를 탐색하려면 카테고리를 선택하세요.',
    browse: '탐색',
    copyright: '모든 권리 보유.',
    adminLogin: '관리자 로그인',
    adminEmail: '이메일',
    adminPassword: '비밀번호',
    adminSubmit: '로그인',
    adminTitle: '관리자 섹션',
    categories: [
      {
        id: 'legal',
        title: '법률 뉴스',
        subtitle: 'Хуулийн мэдээ',
        description: '몽골과 한국의 법원 판결, 법률 업데이트 및 법률 분석.',
        tag: 'Pobtusa - 팝투사 법률 뉴스',
      },
      {
        id: 'financial',
        title: '금융 뉴스',
        subtitle: 'Санхүүгийн мэдээ',
        description: '시장, 경제 정책, 은행 규정 및 투자 인사이트.',
        tag: 'Hantusa - 한투사 금융',
      },
      {
        id: 'other',
        title: '기타 뉴스',
        subtitle: 'Бусад мэдээ',
        description: '지역 전반의 정치, 사회, 기술 및 일반 뉴스.',
        tag: 'Mongtusa - 몽투사',
      },
    ],
  },
};

const categoryStyles = [
  {
    id: 'legal',
    icon: 'ScaleIcon',
    accent: '#8B4513',
    bg: 'from-amber-50 to-orange-50',
    border: 'border-amber-200',
    hover: 'hover:border-amber-400',
    href: '/legal-news',
  },
  {
    id: 'financial',
    icon: 'BanknotesIcon',
    accent: '#1a5c3a',
    bg: 'from-emerald-50 to-teal-50',
    border: 'border-emerald-200',
    hover: 'hover:border-emerald-400',
    href: '/financial-news',
  },
  {
    id: 'other',
    icon: 'NewspaperIcon',
    accent: '#1e3a5f',
    bg: 'from-blue-50 to-slate-50',
    border: 'border-blue-200',
    hover: 'hover:border-blue-400',
    href: '/other-news',
  },
];

export default function LandingPage() {
  const [hovered, setHovered] = useState<string | null>(null);
  const [lang, setLang] = useState<'mn' | 'kr'>('mn');
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('preferredLanguage') as 'mn' | 'kr';
    if (saved === 'mn' || saved === 'kr') setLang(saved);
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as 'mn' | 'kr';
      if (detail === 'mn' || detail === 'kr') setLang(detail);
    };
    window.addEventListener('languageChange', handler);
    return () => window.removeEventListener('languageChange', handler);
  }, []);

  const handleLangChange = (l: 'mn' | 'kr') => {
    setLang(l);
    localStorage.setItem('preferredLanguage', l);
    window.dispatchEvent(new CustomEvent('languageChange', { detail: l }));
  };

  const t = translations[lang];

  return (
    <div className="min-h-screen bg-[#faf9f7] flex flex-col">
      {/* Header */}
      <header className="px-6 py-5 flex items-center justify-between border-b border-stone-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#8B4513] rounded-lg flex items-center justify-center">
            <Icon name="ScaleIcon" size={20} className="text-white" />
          </div>
          <span className="text-lg font-semibold text-stone-800 tracking-tight">Mongtusa</span>
        </div>
        {/* Language Toggle */}
        <div className="flex items-center gap-1 bg-stone-100 rounded-full p-1">
          <button
            onClick={() => handleLangChange('mn')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              lang === 'mn' ? 'bg-[#8B4513] text-white shadow-sm' : 'text-stone-600 hover:bg-white'
            }`}
          >
            🇲🇳 Монгол
          </button>
          <button
            onClick={() => handleLangChange('kr')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              lang === 'kr' ? 'bg-[#8B4513] text-white shadow-sm' : 'text-stone-600 hover:bg-white'
            }`}
          >
            🇰🇷 한국어
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-16">
        {/* Hero */}
        <div className="text-center mb-14 max-w-xl">
          <p className="text-xs font-mono uppercase tracking-widest text-stone-400 mb-4">
            {t.tagline}
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 leading-tight mb-4">
            {t.heading1}<br />
            <span className="text-[#8B4513]">{t.heading2}</span>
          </h1>
          <p className="text-stone-500 text-base">{t.subheading}</p>
        </div>

        {/* Category Cards */}
        <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-3 gap-4">
          {t.categories.map((cat, i) => {
            const style = categoryStyles[i];
            return (
              <Link
                key={cat.id}
                href={style.href}
                onMouseEnter={() => setHovered(cat.id)}
                onMouseLeave={() => setHovered(null)}
                className={`
                  group relative rounded-2xl border-2 ${style.border} ${style.hover}
                  bg-gradient-to-br ${style.bg}
                  p-6 flex flex-col gap-4
                  transition-all duration-300 ease-out
                  ${hovered === cat.id ? 'shadow-xl -translate-y-1 scale-[1.02]' : 'shadow-sm'}
                `}
                style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
              >
                {/* Tag */}
                <span
                  className="self-start text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full"
                  style={{ backgroundColor: style.accent + '18', color: style.accent }}
                >
                  {cat.tag}
                </span>

                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: style.accent + '15' }}
                >
                  <Icon name={style.icon as any} size={26} style={{ color: style.accent }} />
                </div>

                {/* Text */}
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-stone-900 mb-1">{cat.title}</h2>
                  <p className="text-[11px] font-mono text-stone-400 mb-3">{cat.subtitle}</p>
                  <p className="text-sm text-stone-600 leading-relaxed">{cat.description}</p>
                </div>

                {/* Arrow */}
                <div
                  className="flex items-center gap-1 text-sm font-medium transition-all duration-200 group-hover:gap-2"
                  style={{ color: style.accent }}
                >
                  <span>{t.browse}</span>
                  <Icon name="ArrowRightIcon" size={14} />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Admin Login Section */}
        <div className="mt-16 w-full max-w-xs">
          <button
            onClick={() => setShowAdminLogin(!showAdminLogin)}
            className="w-full flex items-center justify-center gap-2 text-xs text-stone-400 hover:text-stone-600 transition-colors duration-200 py-2"
          >
            <Icon name="LockClosedIcon" size={12} />
            <span className="font-mono tracking-wider">{t.adminLogin}</span>
            <Icon name={showAdminLogin ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={12} />
          </button>

          {showAdminLogin && (
            <div className="mt-2 bg-white border border-stone-200 rounded-xl shadow-sm p-5 animate-fade-in">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 bg-[#8B4513] rounded-md flex items-center justify-center">
                  <Icon name="LockClosedIcon" size={12} className="text-white" />
                </div>
                <span className="text-xs font-semibold text-stone-700 tracking-wide">{t.adminTitle}</span>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  window.location.href = '/login';
                }}
                className="flex flex-col gap-3"
              >
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-stone-400 mb-1">
                    {t.adminEmail}
                  </label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#8B4513] focus:border-[#8B4513] text-stone-800 placeholder-stone-300"
                    placeholder="admin@pobtusa.com"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-stone-400 mb-1">
                    {t.adminPassword}
                  </label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#8B4513] focus:border-[#8B4513] text-stone-800"
                    placeholder="••••••••"
                  />
                </div>
                <Link
                  href="/login"
                  className="w-full mt-1 py-2 bg-[#8B4513] hover:bg-[#7a3b10] text-white text-xs font-semibold rounded-lg text-center transition-colors duration-200"
                >
                  {t.adminSubmit}
                </Link>
              </form>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-5 border-t border-stone-100 flex items-center justify-between">
        <p className="text-xs text-stone-400">
          © {new Date().getFullYear()} Mongtusa. {t.copyright}
        </p>
        <div className="flex items-center gap-4 text-xs text-stone-400">
          <span>🇲🇳 · 🇰🇷</span>
        </div>
      </footer>
    </div>
  );
}